import { QueryKey } from '@tanstack/react-query'
import { SubgraphType } from 'config/subgraph'
import type { SubgraphQueryOptions } from 'hooks/subgraph/queryOptions'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import {
  AllV3TicksQuery,
  FeeTierDistributionByIdsQuery,
  FeeTierDistributionQuery,
  GetV3ProtocolQuery,
  PoolsByAddressesQuery,
  ProtocolDayDatasQuery,
  useAllV3TicksQuery,
  useFeeTierDistributionByIdsQuery,
  useFeeTierDistributionQuery,
  useGetV3ProtocolQuery,
  usePoolsByAddressesQuery,
  useProtocolDayDatasQuery,
} from 'queries/v3/generated'
import { useMemo } from 'react'

const v3KeyBase = (chainId: number) => ['v3', chainId] as const

export const v3QueryKeys = {
  poolsByAddresses: (chainId: number, poolAddresses: string[]) =>
    [...v3KeyBase(chainId), 'poolsByAddresses', ...poolAddresses] as const,
  ticks: (chainId: number, poolAddress: string, skip: number) =>
    [...v3KeyBase(chainId), 'ticks', poolAddress, skip] as const,
  feeTierDistribution: (chainId: number, token0: string, token1: string) =>
    [...v3KeyBase(chainId), 'feeTierDistribution', token0, token1] as const,
  feeTierDistributionByIds: (chainId: number, ids: string[]) =>
    [...v3KeyBase(chainId), 'feeTierDistributionByIds', ...ids] as const,
  protocol: (chainId: number) => [...v3KeyBase(chainId), 'protocol'] as const,
  protocolDayDatas: (chainId: number) => [...v3KeyBase(chainId), 'protocolDayDatas'] as const,
  positions: (chainId: number, owner: string, minLiquidity: string, first: number, skip: number, searchTerm?: string) =>
    [...v3KeyBase(chainId), 'positions', owner, minLiquidity, first, skip, searchTerm ?? ''] as const,
  positionById: (chainId: number, id: string) => [...v3KeyBase(chainId), 'positionById', id] as const,
  searchPositionsById: (
    chainId: number,
    owner: string,
    searchId: string,
    minLiquidity: string,
    first: number,
    searchTerm: string
  ) => [...v3KeyBase(chainId), 'searchPositionsById', owner, searchId, minLiquidity, first, searchTerm] as const,
  searchPositions: (chainId: number, owner: string, minLiquidity: string, searchTerm: string) =>
    [...v3KeyBase(chainId), 'searchPositions', owner, minLiquidity, searchTerm] as const,
}

export const useV3PoolsByAddresses = (
  poolAddresses: string[],
  options?: SubgraphQueryOptions<PoolsByAddressesQuery>
) => {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const { skip, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const defaultBehavior = { refetchOnWindowFocus: false, refetchOnReconnect: true }

  const normalizedAddresses = useMemo(
    () => poolAddresses.map((address) => address?.toLowerCase?.() || '').filter(Boolean),
    [poolAddresses]
  )

  const enabled = (optionEnabled ?? true) && !skip && Boolean(endpoint) && normalizedAddresses.length > 0

  return usePoolsByAddressesQuery(
    { endpoint },
    { poolAddresses: normalizedAddresses },
    {
      ...defaultBehavior,
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v3QueryKeys.poolsByAddresses(chainId, normalizedAddresses)) as QueryKey,
    }
  )
}

export const useAllV3Ticks = (
  variables: { poolAddress: string; skip?: number },
  options?: SubgraphQueryOptions<AllV3TicksQuery>
) => {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const { poolAddress, skip = 0 } = variables
  const { skip: skipOption, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const normalizedAddress = poolAddress?.toLowerCase?.() ?? ''
  const enabled = (optionEnabled ?? true) && !skipOption && Boolean(endpoint) && Boolean(normalizedAddress)
  const defaultBehavior = { refetchOnWindowFocus: false, refetchOnReconnect: true }

  return useAllV3TicksQuery(
    { endpoint },
    { poolAddress: normalizedAddress, skip },
    {
      ...defaultBehavior,
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v3QueryKeys.ticks(chainId, normalizedAddress, skip)) as QueryKey,
    }
  )
}

export const useFeeTierDistribution = (
  variables: { token0: string; token1: string },
  options?: SubgraphQueryOptions<FeeTierDistributionQuery>
) => {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const { token0, token1 } = variables
  const { skip, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const normalized0 = token0?.toLowerCase?.() ?? ''
  const normalized1 = token1?.toLowerCase?.() ?? ''
  const enabled = (optionEnabled ?? true) && !skip && Boolean(endpoint) && Boolean(normalized0) && Boolean(normalized1)
  const defaultBehavior = { refetchOnWindowFocus: false, refetchOnReconnect: true }

  return useFeeTierDistributionQuery(
    { endpoint },
    { token0: normalized0, token1: normalized1 },
    {
      ...defaultBehavior,
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v3QueryKeys.feeTierDistribution(chainId, normalized0, normalized1)) as QueryKey,
    }
  )
}

export const useFeeTierDistributionByIds = (
  ids: string[],
  options?: SubgraphQueryOptions<FeeTierDistributionByIdsQuery>
) => {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const { skip, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const normalizedIds = useMemo(
    () =>
      ids
        .map((id) => id?.toLowerCase?.() || '')
        .filter(Boolean)
        .sort(),
    [ids]
  )

  const enabled = (optionEnabled ?? true) && !skip && Boolean(endpoint) && normalizedIds.length > 0
  const defaultBehavior = { refetchOnWindowFocus: false, refetchOnReconnect: true }

  return useFeeTierDistributionByIdsQuery(
    { endpoint },
    { ids: normalizedIds },
    {
      ...defaultBehavior,
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v3QueryKeys.feeTierDistributionByIds(chainId, normalizedIds)) as QueryKey,
    }
  )
}

export const useProtocolDayDatas = (options?: SubgraphQueryOptions<ProtocolDayDatasQuery>) => {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const { skip, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(endpoint)
  const defaultBehavior = { refetchOnWindowFocus: false, refetchOnReconnect: true }

  return useProtocolDayDatasQuery(
    { endpoint },
    {},
    {
      ...defaultBehavior,
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v3QueryKeys.protocolDayDatas(chainId)) as QueryKey,
    }
  )
}

export const useV3Protocol = (
  options?: SubgraphQueryOptions<GetV3ProtocolQuery['factories'][0] | null, GetV3ProtocolQuery>
) => {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const { skip, enabled: optionEnabled, ...queryOptions } = options ?? {}
  const enabled = (optionEnabled ?? true) && !skip && Boolean(endpoint)
  const defaultBehavior = { refetchOnWindowFocus: false, refetchOnReconnect: true }

  return useGetV3ProtocolQuery(
    { endpoint },
    {},
    {
      ...defaultBehavior,
      ...queryOptions,
      enabled,
      queryKey: (options?.queryKey ?? v3QueryKeys.protocol(chainId)) as QueryKey,
      select: (data) =>
        data?.factories?.[0] ?? {
          txCount: '0',
          totalVolumeUSD: '0',
          totalFeesUSD: '0',
          totalValueLockedUSD: '0',
        },
    }
  )
}
